import { useCallback, useEffect, useState } from 'react'
import { Icon } from './Icon'
import { api, type Publication, type SiteBuild } from '../api'
import { useDialog } from '../useDialog'

/**
 * What readers have been shown, and how to take it back.
 *
 * Publishing is the one step in this product a person cannot undo by pressing
 * something else, so this panel is the undo. Three decisions in it:
 *
 * A rollback needs confirming, and the confirmation says what it will do in
 * words — which pages, and that it adds to the history rather than erasing any
 * of it. Somebody opening this panel is usually having a bad day, and "are you
 * sure?" is not information.
 *
 * A refusal is shown in full. The server says things like "pages/x.md changed
 * after that publication" or "that state would break the site now", and those
 * sentences are the answer to the question the person is about to ask.
 *
 * Rollbacks appear in the list like anything else. A record that hid them would
 * stop explaining how the site got where it is, which is the only reason to keep
 * a record at all.
 */
interface Props {
  open: boolean
  onClose: () => void
  /** Whether this person may publish, which is the same grant a rollback needs. */
  mayPublish: boolean
  /** So the shell can refresh the tree after the site moves. */
  onRolledBack: () => void
  /**
   * The door out of a history with nothing in it: the pages, which is where a
   * first publication comes from. Left out for anybody who cannot write, so
   * the empty panel simply says what it says.
   */
  onWrite?: () => void
}

/** "3 minutes ago". Absolute dates make a reader do arithmetic. */
function ago(iso: string): string {
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000)
  const scale: [number, string][] = [
    [60, 'second'],
    [60, 'minute'],
    [24, 'hour'],
    [7, 'day'],
    [4.35, 'week'],
    [12, 'month'],
  ]
  let value = seconds
  let unit = 'second'
  for (const [size, next] of scale) {
    if (value < size) break
    value /= size
    unit = next
  }
  const rounded = Math.floor(value)
  if (unit === 'second' && rounded < 30) return 'just now'
  return `${rounded} ${unit}${rounded === 1 ? '' : 's'} ago`
}

/**
 * What to say about the build that would make a publication visible.
 *
 * `null` means say nothing: an older entry whose build has long since finished
 * does not need a label, and labelling everything would bury the one that
 * matters.
 */
function buildNote(build: SiteBuild | undefined): { text: string; tone: string } | null {
  if (!build) return null
  switch (build.status) {
    case 'queued':
      return { text: 'waiting to build', tone: 'quiet' }
    case 'running':
      return { text: 'building', tone: 'quiet' }
    case 'failed':
      // The one people need. Without it, a failed build looks exactly like a
      // product where nothing happened.
      return { text: 'build failed', tone: 'warn' }
    case 'superseded':
      // Not a failure: a newer publish subsumed it. Saying "failed" here would
      // send somebody looking for a problem that does not exist.
      return { text: 'replaced by a later publish', tone: 'quiet' }
    default:
      return null
  }
}

export function History({ open, onClose, mayPublish, onRolledBack, onWrite }: Props) {
  const panel = useDialog<HTMLElement>(open, onClose)
  const [entries, setEntries] = useState<Publication[] | null>(null)
  const [builds, setBuilds] = useState<SiteBuild[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /** The one being confirmed. Null means nothing is. */
  const [confirming, setConfirming] = useState<string | null>(null)

  /**
   * Ask the queue for a failed build again, then re-read: the row's status is
   * the only honest way to say whether it took.
   */
  const tryBuildAgain = async (id: string) => {
    if (!id) return
    setBusy(true)
    setError(null)
    try {
      await api.retryBuild(id)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const load = useCallback(async () => {
    try {
      const body = await api.publications()
      setEntries(body.publications)
      setBuilds(body.builds)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [])

  useEffect(() => {
    if (open) void load()
    else {
      setConfirming(null)
      setError(null)
    }
  }, [open, load])

  /**
   * Keep looking while a build is in flight.
   *
   * Only then: a panel that polls for ever costs a request every few seconds
   * for as long as somebody leaves it open, and once every build has settled
   * there is nothing left to change.
   */
  const waiting = builds.some((b) => b.status === 'queued' || b.status === 'running')
  useEffect(() => {
    if (!open || !waiting) return
    const timer = window.setInterval(() => void load(), 4000)
    return () => window.clearInterval(timer)
  }, [open, waiting, load])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // Escape backs out of the confirmation first. Closing the whole panel
      // would lose the place somebody was looking at.
      if (confirming) setConfirming(null)
      else onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose, confirming])

  const rollBack = async (id: string) => {
    setBusy(true)
    setError(null)
    try {
      await api.rollback(id)
      setConfirming(null)
      await load()
      onRolledBack()
    } catch (e) {
      // Left open on purpose: the refusal explains what to do next, and closing
      // the confirmation would take that sentence away with it.
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  if (!open) return null

  return (
    <div className="scrim" onMouseDown={onClose}>
      <aside
        className="drawer"
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label="What has been published"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="drawer__head">
          <div>
            <h2 className="drawer__title">Published</h2>
            <p className="drawer__sub">Newest first. Nothing here is ever removed.</p>
          </div>
          <button className="btn btn--ghost btn--icon" onClick={onClose}>
            <Icon name="close" size={15} label="Close" />
          </button>
        </header>

        <div className="drawer__body">
          {error && <p className="panel__error">{error}</p>}

          {/* The shape of the list, held while it is read. Before this the
              body was empty until the answer came back, so the panel opened
              blank and then filled — and a blank panel is what "there is
              nothing here" looks like. */}
          {entries === null && !error && (
            <div className="screen__loading" aria-busy="true">
              <p className="sr-only">Loading…</p>
              <span className="skeleton" />
              <span className="skeleton" />
              <span className="skeleton" />
            </div>
          )}

          {entries !== null && entries.length === 0 && (
            <div className="drawer__none">
              <p className="nothing">
                Nothing has been published yet. When it is, it will be listed here with a way to
                take it back.
              </p>
              {onWrite && (
                <button
                  className="btn btn--tinted btn--sm"
                  onClick={() => {
                    onClose()
                    onWrite()
                  }}
                >
                  <Icon name="page" size={13} />
                  Open Pages
                </button>
              )}
            </div>
          )}

          <ol className="pubs">
            {(entries ?? []).map((entry) => {
              const undone = entries?.some((other) => other.reverts === entry.id) ?? false
              const note = buildNote(builds.find((b) => b.publication === entry.id))
              return (
                <li key={entry.id} className="pubs__row">
                  <span className="pubs__mark" aria-hidden="true">
                    <Icon name={entry.kind === 'rollback' ? 'undo' : 'commit'} size={13} />
                  </span>
                  <div className="pubs__what">
                    <div className="pubs__title truncate">{entry.message}</div>
                    <div className="pubs__meta">
                      {entry.actorName ?? 'somebody'} · {ago(entry.createdAt)} ·{' '}
                      <span className="mono">{entry.sha.slice(0, 7)}</span>
                      {/*
                        Where it landed, when that is not the live site. A
                        publish into a frozen version sat in this list looking
                        exactly like one readers saw, and the weekly letter
                        counted it as one; the row knows now, and says.
                      */}
                      {entry.intoRef?.startsWith('version/') && (
                        <>
                          {' · '}
                          <span data-into={entry.intoRef}>
                            into version {entry.intoRef.slice('version/'.length)}
                          </span>
                        </>
                      )}
                    </div>
                    {note && (
                      <div className={`pubs__build pubs__build--${note.tone}`}>
                        {note.text}
                        {note.tone === 'warn' && (
                          <>
                            {' — '}
                            <span className="pubs__why">
                              {
                                builds
                                  .find((b) => b.publication === entry.id)
                                  ?.error?.split('\n')[0]
                              }
                            </span>
                            {/*
                              The way back. The queue gives up after three
                              attempts, and until this existed the only way to
                              ask again was to publish something else — so
                              somebody whose site had not changed edited
                              content to shake it loose.
                            */}
                            {mayPublish && (
                              <button
                                className="btn btn--ghost btn--sm"
                                data-build-retry
                                disabled={busy}
                                onClick={() =>
                                  void tryBuildAgain(
                                    builds.find((b) => b.publication === entry.id)?.id ?? '',
                                  )
                                }
                              >
                                Try the build again
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    )}
                    <div className="pubs__paths">
                      {/* "changes", not "files": a publication carries pages and
                          the navigation together, and `files` is the storage
                          engine's word for both. */}
                      {entry.paths.length === 0
                        ? 'nothing changed'
                        : entry.paths.length === 1
                          ? entry.paths[0]
                          : `${entry.paths.length} changes`}
                    </div>

                    {confirming === entry.id && (
                      <div className="pubs__confirm">
                        <p className="drawer__hint">
                          This puts{' '}
                          {entry.paths.length === 1
                            ? entry.paths[0]
                            : `${entry.paths.length} changes`}{' '}
                          back to how they were before this went out. It adds a new entry here — it
                          does not remove this one.
                        </p>
                        <div className="pubs__actions">
                          <button
                            className="btn btn--tinted btn--sm"
                            disabled={busy}
                            onClick={() => void rollBack(entry.id)}
                          >
                            Roll it back
                          </button>
                          <button
                            className="btn btn--ghost btn--sm"
                            disabled={busy}
                            onClick={() => setConfirming(null)}
                          >
                            Keep it
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {undone ? (
                    /* The sentence was in a `title` on this span, which is a
                       mouse affordance on an element nothing can focus — so
                       "rolled back" stood alone for a keyboard, and it is
                       ambiguous alone: it reads as easily as "this WAS a
                       rollback" as "this was undone". The clarifier is in the
                       badge's own text now, out of sight and in the
                       accessible name. */
                    <span className="badge badge--quiet">
                      rolled back
                      <span className="sr-only"> — a later entry undid this</span>
                    </span>
                  ) : (
                    mayPublish &&
                    entry.previousSha !== null &&
                    confirming !== entry.id && (
                      <button
                        className="btn btn--ghost btn--sm"
                        disabled={busy}
                        onClick={() => setConfirming(entry.id)}
                        data-tip="Put these files back to how they were before this"
                      >
                        Roll back
                      </button>
                    )
                  )}
                </li>
              )
            })}
          </ol>
        </div>
      </aside>
    </div>
  )
}
